import { useState } from "react";
import { Mail, X } from "lucide-react";
import { toast } from "react-toastify";
import { useUserContext } from "../context/UserContext";
import { useTranslation } from "../context/LanguageContext";
import { optInMarketingEmails, resolveNotificationPreferences } from "../services/UserService";
import { track } from "../utils/analytics";

const DISMISS_KEY = "dealhawk:emailOptInCardDismissed";

function readDismissed(): boolean {
  try {
    return window.localStorage.getItem(DISMISS_KEY) === "1";
  } catch {
    return false;
  }
}

function writeDismissed() {
  try {
    window.localStorage.setItem(DISMISS_KEY, "1");
  } catch {
    // Storage blocked: the card simply reappears next visit.
  }
}

// Dismissible prompt for signed-in users who have marketing email off.
export default function EmailOptInCard() {
  const { user, load, isGodMode } = useUserContext();
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(readDismissed);
  const [saving, setSaving] = useState(false);

  if (!user || isGodMode || dismissed || resolveNotificationPreferences(user).marketingEmails) return null;

  const dismiss = () => {
    writeDismissed();
    setDismissed(true);
    track("email_optin_card_dismissed");
  };

  const turnOn = async () => {
    setSaving(true);
    try {
      await optInMarketingEmails("in-app-card");
      track("email_optin_card_accepted");
      toast.success(t("products.emailOptInCardDone"));
      writeDismissed();
      setDismissed(true);
      load();
    } catch (error) {
      console.error("Failed to turn on marketing email", error);
      toast.error(t("products.notificationPrefFailed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-2 mt-2 flex items-center gap-3 rounded-xl border border-purple-200 bg-white px-3 py-2.5 shadow-sm dark:border-purple-800/50 dark:bg-dark-900">
      <Mail className="h-4 w-4 flex-none text-purple-600 dark:text-purple-300" />
      <p className="min-w-0 flex-1 text-sm font-medium text-gray-900 dark:text-white">{t("products.emailOptInCardTitle")}</p>
      <button
        type="button"
        onClick={turnOn}
        disabled={saving}
        className="flex-none rounded-lg bg-purple-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-purple-700 disabled:opacity-60"
      >
        {t("products.emailOptInCardButton")}
      </button>
      <button
        type="button"
        onClick={dismiss}
        className="flex-none rounded-lg p-1 text-gray-400 transition-colors hover:text-gray-600 dark:hover:text-dark-100"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
